import React, { useCallback, useEffect, useMemo, useState } from "react";

import { useFormContext, useWatch } from "react-hook-form";

import { useAgentVersionConfigPanel } from "src/agents/AgentVersionPage/AgentVersionConfigPanelContext";
import {
  SideDrawer,
  useSideDrawer,
  useSideDrawerCoordinator,
} from "src/agents/base/SideDrawer";
import { AgentVersionConfigurationInput } from "src/agents/types";
import { Schema } from "src/api/flowTypes";
import { Button } from "src/base-components/Button";
import { CodeEditor } from "src/base-components/CodeInput/CodeEditor";
import { Callout } from "src/design-system/Callout";
import { JsonSchemaUnsavedChangesModal } from "src/jsonSchema/editor/JsonSchemaUnsavedChangesModal";
import {
  normalizeJsonSchema,
  normalizeNullableUnions,
  normalizeNumericConstraints,
} from "src/jsonSchema/normalizeJsonSchema";
import {
  isEmptySchemaString,
  noAllOf,
  noNestedObjects,
  ValidationConfig,
  validateJsonSchema,
} from "src/jsonSchema/validateJsonSchema";
import { defaultSchema } from "src/schema/schemaMappingUtils";

type SchemaType = "input" | "output";

const defaultNormalizers = [
  normalizeNumericConstraints,
  normalizeNullableUnions,
];

// ============================================================================
// Metadata Preservation
// ============================================================================

type PreservedMetadata = {
  sensitive?: string[];
  order?: string[];
};

/**
 * Extracts metadata (sensitive, order) from a schema that should be preserved
 * when the user edits the schema via JSON. These fields are stripped from the
 * displayed JSON but need to be merged back when saving.
 */
const extractPreservedMetadata = (
  schema: Schema | null,
): PreservedMetadata | null => {
  if (!schema) return null;

  const metadata: PreservedMetadata = {};

  if (schema.sensitive?.length) {
    metadata.sensitive = [...schema.sensitive];
  }
  if (schema.order?.length) {
    metadata.order = [...schema.order];
  }

  return metadata.sensitive || metadata.order ? metadata : null;
};

/**
 * Merges preserved metadata back into a schema after JSON editing.
 * - Filters out orphaned entries (fields that no longer exist)
 * - Appends new fields to the end of the order array
 */
const mergePreservedMetadata = (
  newSchema: Schema,
  metadata: PreservedMetadata | null,
): Schema => {
  if (!metadata) return newSchema;

  const result = { ...newSchema };
  const propNames = new Set(Object.keys(result.properties ?? {}));

  // Merge sensitive - filter to only include existing properties
  if (metadata.sensitive) {
    const validSensitive = metadata.sensitive.filter((name) =>
      propNames.has(name),
    );
    if (validSensitive.length > 0) {
      result.sensitive = validSensitive;
    }
  }

  // Merge order - keep existing properties in their order, append new ones at end
  if (metadata.order) {
    const orderedExisting = metadata.order.filter((name) =>
      propNames.has(name),
    );
    const newProps = [...propNames].filter(
      (name) => !metadata.order!.includes(name),
    );
    result.order = [...orderedExisting, ...newProps];
  }

  return result;
};

/**
 * Strips metadata fields (sensitive, order) from a schema while preserving
 * user-defined properties with the same names.
 *
 * Distinguishes between:
 * - Metadata: sensitive/order as string arrays
 * - User properties: sensitive/order as object definitions
 */
const stripSchemaMetadata = <T extends object>(obj: T): T => {
  if (obj === null || obj === undefined) {
    return obj;
  }

  if (Array.isArray(obj)) {
    return obj.map((item) => stripSchemaMetadata(item)) as T;
  }

  if (typeof obj !== "object") {
    return obj;
  }

  const result: Record<string, unknown> = {};

  for (const [key, value] of Object.entries(obj)) {
    // Always skip internal fields
    if (["_schema", "_schemaNode", "_isNew"].includes(key)) {
      continue;
    }

    // Strip sensitive/order only if they are metadata arrays
    if (key === "sensitive" || key === "order") {
      const isMetadataArray =
        Array.isArray(value) && value.every((item) => typeof item === "string");

      if (isMetadataArray) {
        continue; // Skip metadata
      }
    }

    // Recursively process all other values
    result[key] = stripSchemaMetadata(value);
  }

  return result as T;
};

/**
 * Converts a schema to a clean JSON string for editing.
 * Removes metadata arrays and internal fields.
 */
const schemaToJsonString = (schema: Schema | null): string => {
  if (!schema) {
    return JSON.stringify(
      {
        $schema: "https://json-schema.org/draft/2020-12/schema",
        type: "object",
        properties: {},
        required: [],
      },
      null,
      2,
    );
  }
  const cleanSchema = stripSchemaMetadata(schema);
  return JSON.stringify(cleanSchema, null, 2);
};

export const JsonSchemaDrawer: React.FC = () => {
  const { openDrawer } = useSideDrawerCoordinator();
  const schemaType =
    openDrawer?.type === "jsonSchema" ? openDrawer.data.schemaType : null;
  const readOnly =
    openDrawer?.type === "jsonSchema" ? !!openDrawer.data.readOnly : false;

  const schemaLabel = schemaType === "input" ? "input" : "output";
  const title = readOnly
    ? `View ${schemaLabel} schema`
    : `Edit ${schemaLabel} schema`;
  const description = readOnly
    ? "Read-only view of the JSON schema"
    : "Paste your JSON schema below";

  return (
    <SideDrawer
      dataLoc="json-schema-sidebar"
      drawerId="jsonSchema"
      header={{
        title,
        description,
        closeDataLoc: "close-json-schema-sidebar",
      }}
    >
      {schemaType && (
        <JsonSchemaDrawerContent readOnly={readOnly} schemaType={schemaType} />
      )}
    </SideDrawer>
  );
};

type JsonSchemaDrawerContentProps = {
  schemaType: SchemaType;
  readOnly?: boolean;
};

/**
 * Content component for the JSON schema drawer.
 * Accesses form context directly to read/write schema.
 */
const JsonSchemaDrawerContent: React.FC<JsonSchemaDrawerContentProps> = ({
  schemaType,
  readOnly = false,
}) => {
  const { jsonSchemaDrawerEditing, setJsonSchemaDrawerEditing } =
    useAgentVersionConfigPanel();

  const { setValue, control } =
    useFormContext<AgentVersionConfigurationInput>();
  const fieldName = schemaType === "input" ? "input_schema" : "output_schema";
  const formSchema = useWatch({ name: fieldName, control }) as Schema | null;

  // Convert form schema to JSON string for editing
  const externalSchemaJson = useMemo(
    () => schemaToJsonString(formSchema),
    [formSchema],
  );

  // Derive dirty state from context
  const isDirty = jsonSchemaDrawerEditing === schemaType;

  // Internal state for the editor
  const [internalSchema, setInternalSchema] = useState(externalSchemaJson);
  const [validationError, setValidationError] = useState<string | null>(null);
  // Confirmation modal state
  const [showConfirmModal, setShowConfirmModal] = useState(false);
  // Preserved metadata (sensitive, order) from original schema
  const [preservedMetadata, setPreservedMetadata] =
    useState<PreservedMetadata | null>(() =>
      extractPreservedMetadata(formSchema),
    );

  // Define validation config based on schema type
  const validationConfig: ValidationConfig = useMemo(() => {
    const customValidators =
      schemaType === "input" ? [noAllOf, noNestedObjects] : [noAllOf];
    return {
      customValidators,
      normalizers: defaultNormalizers,
    };
  }, [schemaType]);

  // Register close handler directly - content component owns the close logic
  const { notifyCloseAccepted, notifyCloseRejected } = useSideDrawer(
    "jsonSchema",
    {
      onCloseRequested: (accept, _reject) => {
        if (jsonSchemaDrawerEditing === schemaType) {
          setShowConfirmModal(true);
        } else {
          accept(); // Not dirty, just close
        }
      },
    },
  );

  // Sync external schema to internal when not dirty
  // This handles the case where form data changes externally while drawer is open
  useEffect(() => {
    if (!isDirty) {
      setInternalSchema(externalSchemaJson);
      // Capture metadata from the current schema for preservation on save
      setPreservedMetadata(extractPreservedMetadata(formSchema));
      // Validate synced schema
      const error = validateJsonSchema(externalSchemaJson, validationConfig);
      setValidationError(error?.message ?? null);
    }
  }, [externalSchemaJson, formSchema, isDirty, validationConfig]);

  const handleSave = useCallback(() => {
    if (isEmptySchemaString(internalSchema)) {
      const schemaWithMetadata = mergePreservedMetadata(
        defaultSchema,
        preservedMetadata,
      );
      setValue(fieldName, schemaWithMetadata);
      setJsonSchemaDrawerEditing(false);
      setShowConfirmModal(false);
      notifyCloseAccepted();
      return;
    }

    const error = validateJsonSchema(internalSchema, validationConfig);
    setValidationError(error?.message ?? null);
    if (error) {
      return; // Prevent save if invalid
    }

    // Parse, normalize, and save to form
    try {
      const parsedSchema = JSON.parse(internalSchema) as Schema;
      // Apply normalizers to fix common issues (e.g., string numbers)
      const normalizedSchema = normalizeJsonSchema(
        parsedSchema,
        validationConfig.normalizers ?? [],
      ) as Schema;
      // Merge back preserved metadata (sensitive, order) that was stripped for display
      const schemaWithMetadata = mergePreservedMetadata(
        normalizedSchema,
        preservedMetadata,
      );
      setValue(fieldName, schemaWithMetadata);
      setJsonSchemaDrawerEditing(false);
      setShowConfirmModal(false);
      notifyCloseAccepted();
    } catch {
      setValidationError("Invalid JSON");
    }
  }, [
    internalSchema,
    validationConfig,
    preservedMetadata,
    setValue,
    fieldName,
    setJsonSchemaDrawerEditing,
    notifyCloseAccepted,
  ]);

  const handleDiscard = useCallback(() => {
    // Reset internal to external (discard user's changes)
    setInternalSchema(externalSchemaJson);
    setValidationError(null);
    setJsonSchemaDrawerEditing(false);
    setShowConfirmModal(false);
    notifyCloseAccepted();
  }, [externalSchemaJson, setJsonSchemaDrawerEditing, notifyCloseAccepted]);

  // "Keep editing" - just close the modal and return to editing
  const handleKeepEditing = useCallback(() => {
    setShowConfirmModal(false);
    notifyCloseRejected();
  }, [notifyCloseRejected]);

  const handleChange = useCallback(
    (value: string) => {
      setInternalSchema(value);
      setJsonSchemaDrawerEditing(schemaType);

      // Validate on change with schema-type-specific validators
      const error = validateJsonSchema(value, validationConfig);
      setValidationError(error?.message ?? null);
    },
    [schemaType, setJsonSchemaDrawerEditing, validationConfig],
  );

  return (
    <div className="flex h-full flex-col">
      {/* Validation Error */}
      {validationError && (
        <div className="mx-4 mt-4">
          <Callout type="error">{validationError}</Callout>
        </div>
      )}

      {/* Code Editor */}
      <div className="decideScrollbar flex-1 overflow-auto p-4">
        <CodeEditor
          backgroundColor="white"
          // Disable CodeMirror's built-in autocomplete dropdowns; they offer no
          // useful suggestions for free-form JSON schema editing.
          basicSetup={{ autocompletion: false }}
          dataLoc="json-schema-editor"
          disabled={readOnly}
          drawErrorBorder={!!validationError}
          language="jsonschema"
          value={internalSchema}
          autoFocus={!readOnly}
          highlightOnFocus={!readOnly}
          onChange={handleChange}
        />
      </div>

      {/* Footer - hidden in read-only mode */}
      {!readOnly && (
        <div className="border-gray-200 border-t px-4 py-3">
          <Button
            dataLoc="save-json-schema"
            disabled={!!validationError || !isDirty}
            onClick={handleSave}
          >
            Save
          </Button>
        </div>
      )}

      {/* Confirmation Modal */}
      <JsonSchemaUnsavedChangesModal
        canSave={!validationError}
        open={showConfirmModal}
        onDiscard={handleDiscard}
        onKeepEditing={handleKeepEditing}
        onSave={handleSave}
      />
    </div>
  );
};
